import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Mail, FileText, Send, Eye, RefreshCcw, Save, Trash2, Plus, Search,
  X, Bell, Users, User, Globe, Loader2, Check,
} from "lucide-react";

/**
 * AdminCommunicationsPage — central hub for transactional templates,
 * saved custom-message templates, and broadcast send.
 *
 * Three tabs:
 *   1. Transactional Templates: list, preview, edit subject + body.
 *   2. Send Custom: pick audience + channel + saved template (or write
 *      fresh), preview, then send.
 *   3. Saved Templates: CRUD for reusable admin-authored templates.
 */
export default function AdminCommunicationsPage() {
  const [tab, setTab] = useState("transactional");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-comms-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Communications</h1>
        <p className="text-sm text-gray-500 mt-1">
          Transactional emails, custom broadcasts, and reusable templates.
        </p>
      </div>

      <div className="mt-5 flex items-center gap-2 border-b border-gray-200">
        <Tab active={tab === "transactional"} onClick={() => setTab("transactional")} testId="comms-tab-transactional">
          Transactional Templates
        </Tab>
        <Tab active={tab === "send"} onClick={() => setTab("send")} testId="comms-tab-send">
          Send Custom Message
        </Tab>
        <Tab active={tab === "saved"} onClick={() => setTab("saved")} testId="comms-tab-saved">
          Saved Templates
        </Tab>
      </div>

      <div className="mt-6">
        {tab === "transactional" && <TransactionalTemplatesTab />}
        {tab === "send" && <SendCustomTab />}
        {tab === "saved" && <SavedTemplatesTab />}
      </div>
    </div>
  );
}

const Tab = ({ active, onClick, testId, children }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
      active
        ? "border-teal-600 text-teal-700"
        : "border-transparent text-gray-500 hover:text-gray-800"
    }`}
  >
    {children}
  </button>
);

// ──────────────────────────────────────────────────────────────────────
// TAB 1: Transactional Templates — list + edit overrides
// ──────────────────────────────────────────────────────────────────────

function TransactionalTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // template object or null
  const [previewing, setPreviewing] = useState(null);  // { mail_type } or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/email-templates`, { withCredentials: true });
      setTemplates(r.data?.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load templates");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-sm text-gray-400">Loading...</p>;

  return (
    <div className="space-y-3" data-testid="comms-transactional-list">
      <p className="text-xs text-gray-500">
        Override the subject and body for any auto-sent email. Use <code className="bg-gray-100 px-1.5 rounded text-[11px] font-mono">{"{{first_name}}"}</code> style placeholders for variable substitution.
      </p>
      {templates.map(t => (
        <Card key={t.mail_type} className="border-gray-200 shadow-none" data-testid={`tmpl-${t.mail_type}`}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-teal-600" />
                  <h3 className="font-semibold text-gray-900 text-sm">{t.label}</h3>
                  {t.has_override && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                      <Check className="w-2.5 h-2.5" />Override active
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{t.description}</p>
                <p className="text-[11px] text-gray-400 mt-1.5">
                  <span className="font-medium">Trigger:</span> {t.trigger}
                </p>
                <div className="text-[11px] text-gray-400 mt-1">
                  <span className="font-medium">Variables:</span>{" "}
                  {t.variables.map(v => (
                    <code key={v} className="bg-gray-100 px-1 rounded mr-1 font-mono text-[10px]">{`{{${v}}}`}</code>
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-1.5 flex-shrink-0">
                <Button variant="outline" size="sm" onClick={() => setPreviewing(t)} data-testid={`tmpl-preview-${t.mail_type}`}>
                  <Eye className="w-3.5 h-3.5" />Preview
                </Button>
                <Button size="sm" onClick={() => setEditing(t)} data-testid={`tmpl-edit-${t.mail_type}`}>
                  <FileText className="w-3.5 h-3.5" />Edit
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {editing && (
        <TransactionalEditModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {previewing && (
        <PreviewModal mailType={previewing.mail_type} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

function TransactionalEditModal({ template, onClose, onSaved }) {
  const [subject, setSubject] = useState(template.subject || "");
  const [body, setBody] = useState(template.body || "");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/email-templates/${template.mail_type}`,
        { subject: subject || null, body: body || null },
        { withCredentials: true },
      );
      toast.success("Override saved.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Reset this template to the code default? Your custom copy will be lost.")) return;
    setResetting(true);
    try {
      await axios.put(
        `${API}/admin/email-templates/${template.mail_type}`,
        {},
        { withCredentials: true },
      );
      toast.success("Reset to default.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset");
    } finally { setResetting(false); }
  };

  return (
    <ModalShell onClose={onClose} title={`Edit: ${template.label}`} testId="tmpl-edit-modal">
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          Leave a field blank to inherit the code default. Use{" "}
          {template.variables.map(v => (
            <code key={v} className="bg-gray-100 px-1 rounded mr-1 font-mono text-[10px]">{`{{${v}}}`}</code>
          ))}
          for variables.
        </p>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Subject</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Leave blank to use the default"
            data-testid="tmpl-edit-subject"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Body</label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="HTML or plain text. Leave blank to use the default."
            rows={12}
            className="font-mono text-xs"
            data-testid="tmpl-edit-body"
          />
        </div>
      </div>
      <div className="flex items-center justify-between mt-5 pt-4 border-t">
        <Button variant="outline" size="sm" onClick={resetToDefault} disabled={resetting || !template.has_override} data-testid="tmpl-reset-btn">
          <RefreshCcw className="w-3.5 h-3.5" />
          {resetting ? "Resetting..." : "Reset to default"}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="tmpl-save-btn">
            <Save className="w-3.5 h-3.5" />{saving ? "Saving..." : "Save override"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function PreviewModal({ mailType, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.post(
          `${API}/admin/email-templates/${mailType}/preview`,
          {}, { withCredentials: true },
        );
        setPreview(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Preview failed");
        onClose();
      } finally { setLoading(false); }
    })();
  }, [mailType, onClose]);

  return (
    <ModalShell onClose={onClose} title="Email preview" testId="tmpl-preview-modal" size="lg">
      {loading ? (
        <p className="text-sm text-gray-400">Rendering...</p>
      ) : preview ? (
        <div className="space-y-3">
          <div className="text-xs"><span className="font-semibold text-gray-700">Subject:</span> <span className="text-gray-800">{preview.subject}</span></div>
          <iframe
            srcDoc={preview.html}
            className="w-full h-[500px] border border-gray-200 rounded-lg bg-white"
            title="Email preview"
            data-testid="tmpl-preview-iframe"
          />
        </div>
      ) : null}
    </ModalShell>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TAB 2: Send Custom Message
// ──────────────────────────────────────────────────────────────────────

function SendCustomTab() {
  const [audienceMode, setAudienceMode] = useState("single");  // single | multi | all | plan
  const [planFilter, setPlanFilter] = useState("trial");
  const [pickedUsers, setPickedUsers] = useState([]);  // [{user_id, email, name}]
  const [channel, setChannel] = useState("both");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const loadSaved = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/comms-templates`, { withCredentials: true });
      setSavedTemplates(r.data?.templates || []);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const applySavedTemplate = (tid) => {
    setSelectedTemplateId(tid);
    if (!tid) return;
    const t = savedTemplates.find(s => s.id === tid);
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
      toast.success(`Template "${t.name}" loaded`);
    }
  };

  const send = async () => {
    if (!subject || !body) return toast.error("Subject and body are required");
    if (audienceMode === "single" && pickedUsers.length !== 1) return toast.error("Pick exactly one user");
    if (audienceMode === "multi" && pickedUsers.length === 0) return toast.error("Pick at least one user");

    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/comms/send`, {
        subject, body, channel, audience_mode: audienceMode,
        user_ids: ["single", "multi"].includes(audienceMode) ? pickedUsers.map(u => u.user_id) : [],
        plan_filter: audienceMode === "plan" ? planFilter : null,
        template_id_used: selectedTemplateId || null,
      }, { withCredentials: true });
      toast.success(
        `Sent to ${r.data.recipient_count} user(s) — ` +
        `email: ${r.data.email_sent} · in-app: ${r.data.in_app_sent}` +
        (r.data.email_failed ? ` · failed: ${r.data.email_failed}` : ""),
      );
      setSubject(""); setBody(""); setPickedUsers([]); setSelectedTemplateId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="comms-send-tab">
      {/* LEFT: form */}
      <div className="space-y-4">
        {/* Audience */}
        <div>
          <label className="text-xs font-semibold text-gray-700 mb-2 block">Audience</label>
          <div className="grid grid-cols-4 gap-1.5">
            <AudPill icon={User} label="Single" active={audienceMode === "single"} onClick={() => setAudienceMode("single")} testId="aud-single" />
            <AudPill icon={Users} label="Multiple" active={audienceMode === "multi"} onClick={() => setAudienceMode("multi")} testId="aud-multi" />
            <AudPill icon={Globe} label="All users" active={audienceMode === "all"} onClick={() => setAudienceMode("all")} testId="aud-all" />
            <AudPill icon={Bell} label="By plan" active={audienceMode === "plan"} onClick={() => setAudienceMode("plan")} testId="aud-plan" />
          </div>
          {(audienceMode === "single" || audienceMode === "multi") && (
            <UserPicker
              picked={pickedUsers}
              onPick={(u) => {
                if (audienceMode === "single") setPickedUsers([u]);
                else if (!pickedUsers.find(p => p.user_id === u.user_id)) setPickedUsers([...pickedUsers, u]);
              }}
              onRemove={(uid) => setPickedUsers(pickedUsers.filter(p => p.user_id !== uid))}
              maxOne={audienceMode === "single"}
            />
          )}
          {audienceMode === "plan" && (
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="mt-2 w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-white"
              data-testid="aud-plan-select"
            >
              <option value="trial">Trial users</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
            </select>
          )}
          {audienceMode === "all" && (
            <p className="text-[11px] text-amber-700 mt-2 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded">
              ⚠️ This will send to ALL active users. Use sparingly.
            </p>
          )}
        </div>

        {/* Channel */}
        <div>
          <label className="text-xs font-semibold text-gray-700 mb-2 block">Channel</label>
          <div className="grid grid-cols-3 gap-1.5">
            <AudPill icon={Mail} label="Email only" active={channel === "email"} onClick={() => setChannel("email")} testId="ch-email" />
            <AudPill icon={Bell} label="In-app only" active={channel === "in_app"} onClick={() => setChannel("in_app")} testId="ch-inapp" />
            <AudPill icon={Send} label="Both" active={channel === "both"} onClick={() => setChannel("both")} testId="ch-both" />
          </div>
        </div>

        {/* Saved templates picker */}
        {savedTemplates.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Load from saved template (optional)</label>
            <select
              value={selectedTemplateId}
              onChange={(e) => applySavedTemplate(e.target.value)}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-white"
              data-testid="comms-saved-select"
            >
              <option value="">— Write fresh —</option>
              {savedTemplates.map(t => (
                <option key={t.id} value={t.id}>{t.name} <em>({t.category})</em></option>
              ))}
            </select>
          </div>
        )}

        {/* Subject + body */}
        <div>
          <label className="text-xs font-semibold text-gray-700 mb-1 block">Subject</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g., New tutorial: building decks 5x faster"
            data-testid="comms-subject"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Variables: <code className="bg-gray-100 px-1 rounded">{"{{first_name}}"}</code>, <code className="bg-gray-100 px-1 rounded">{"{{name}}"}</code>, <code className="bg-gray-100 px-1 rounded">{"{{email}}"}</code>
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 mb-1 block">Body</label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Plain text or HTML. Line breaks become paragraphs."
            rows={10}
            className="text-sm"
            data-testid="comms-body"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} disabled={!subject || !body} data-testid="comms-preview-btn">
            <Eye className="w-4 h-4" />Preview
          </Button>
          <Button onClick={send} disabled={sending || !subject || !body} className="ml-auto bg-teal-600 hover:bg-teal-700 text-white" data-testid="comms-send-btn">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" />Sending</> : <><Send className="w-4 h-4" />Send now</>}
          </Button>
        </div>
      </div>

      {/* RIGHT: live preview pane */}
      <div className="border border-gray-200 rounded-xl bg-gray-50 p-4" data-testid="comms-live-preview">
        <p className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider mb-2">Live preview (sample data)</p>
        <div className="bg-white rounded border border-gray-200 p-4 text-sm">
          <p className="text-xs text-gray-500 mb-1"><span className="font-semibold">Subject:</span> {subject || <span className="italic text-gray-400">empty</span>}</p>
          <hr className="my-2" />
          <div
            className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed"
            dangerouslySetInnerHTML={{ __html: substitutePreview(body) }}
          />
        </div>
        {pickedUsers.length > 0 && (
          <p className="text-[11px] text-gray-500 mt-3">
            Will be sent to {pickedUsers.length} user{pickedUsers.length > 1 ? "s" : ""}.
          </p>
        )}
      </div>

      {previewOpen && (
        <ModalShell title="Preview as user" onClose={() => setPreviewOpen(false)} testId="comms-preview-modal" size="lg">
          <iframe
            srcDoc={`<!DOCTYPE html><html><body style="margin:0;padding:24px;background:#f8fafc;font-family:system-ui;"><div style="background:white;padding:24px;border-radius:12px;border:1px solid #e2e8f0;max-width:560px;margin:0 auto;"><h2 style="font-size:18px;margin:0 0 12px 0;">${escape(subject)}</h2><div>${substitutePreview(body)}</div></div></body></html>`}
            className="w-full h-[500px] border border-gray-200 rounded-lg bg-white"
            title="Custom preview"
          />
        </ModalShell>
      )}
    </div>
  );
}

// Pretend-substitute placeholders with friendly sample values for the
// live preview pane. Server-side, the real `_render_handlebars` runs
// per-recipient, so this is just for what-it-looks-like preview UX.
function substitutePreview(text) {
  return (text || "")
    .replace(/\{\{\s*first_name\s*\}\}/g, "Sam")
    .replace(/\{\{\s*name\s*\}\}/g, "Sam Patel")
    .replace(/\{\{\s*email\s*\}\}/g, "sam@example.com")
    .replace(/\n/g, "<br/>");
}
function escape(s) { return (s || "").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c])); }

const AudPill = ({ icon: Icon, label, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-md border text-xs font-medium transition-all ${
      active ? "bg-teal-50 border-teal-300 text-teal-800" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

function UserPicker({ picked, onPick, onRemove, maxOne }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = useMemo(() => {
    let timer;
    return (qval) => {
      clearTimeout(timer);
      if (!qval || qval.length < 2) { setResults([]); return; }
      timer = setTimeout(async () => {
        setSearching(true);
        try {
          const r = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(qval)}`, { withCredentials: true });
          setResults(r.data?.users || []);
        } catch { setResults([]); }
        finally { setSearching(false); }
      }, 250);
    };
  }, []);

  return (
    <div className="mt-2 space-y-2">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
          placeholder="Search users by email or name..."
          className="pl-9"
          data-testid="user-picker-search"
        />
        {searching && <Loader2 className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
      </div>
      {results.length > 0 && (
        <div className="border border-gray-200 rounded-md bg-white max-h-40 overflow-y-auto" data-testid="user-picker-results">
          {results.map(u => (
            <button
              key={u.user_id}
              onClick={() => { onPick(u); setQ(""); setResults([]); }}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between text-sm border-b border-gray-100 last:border-0"
              data-testid={`user-pick-${u.user_id}`}
              disabled={maxOne && picked.length > 0}
            >
              <span>
                <span className="font-medium text-gray-800">{u.name || u.email}</span>
                <span className="text-xs text-gray-400 ml-2">{u.email}</span>
              </span>
              <span className="text-[10px] uppercase text-gray-400">{u.account_type}</span>
            </button>
          ))}
        </div>
      )}
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="user-picker-chips">
          {picked.map(u => (
            <span key={u.user_id} className="inline-flex items-center gap-1 bg-teal-50 border border-teal-200 text-teal-800 text-xs px-2 py-1 rounded-full">
              {u.name || u.email}
              <button onClick={() => onRemove(u.user_id)} className="hover:text-teal-950">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// TAB 3: Saved Templates — CRUD
// ──────────────────────────────────────────────────────────────────────

function SavedTemplatesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);  // template object or { name, subject, body, category } for new

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/comms-templates`, { withCredentials: true });
      setRows(r.data?.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load templates");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/admin/comms-templates/${id}`, { withCredentials: true });
      toast.success("Template deleted.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="comms-saved-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Reusable templates for tutorials, marketing announcements, etc.</p>
        <Button size="sm" onClick={() => setEditing({ name: "", subject: "", body: "", category: "general" })} data-testid="comms-saved-new-btn">
          <Plus className="w-3.5 h-3.5" />New template
        </Button>
      </div>
      {rows.length === 0 && (
        <p className="text-sm text-gray-400 py-3">No saved templates yet — create one to reuse it in custom sends.</p>
      )}
      {rows.map(t => (
        <Card key={t.id} className="border-gray-200 shadow-none" data-testid={`saved-${t.id}`}>
          <CardContent className="pt-3 pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">{t.name}</h3>
                  <span className="text-[10px] uppercase font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{t.category}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">{t.subject}</p>
              </div>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" onClick={() => setEditing(t)} data-testid={`saved-edit-${t.id}`}>Edit</Button>
                <Button variant="outline" size="sm" onClick={() => remove(t.id)} data-testid={`saved-del-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {editing && (
        <SavedTemplateModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SavedTemplateModal({ template, onClose, onSaved }) {
  const isNew = !template.id;
  const [name, setName] = useState(template.name || "");
  const [category, setCategory] = useState(template.category || "general");
  const [subject, setSubject] = useState(template.subject || "");
  const [body, setBody] = useState(template.body || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name || !subject || !body) return toast.error("All fields required");
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/admin/comms-templates`, { name, subject, body, category }, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/comms-templates/${template.id}`, { name, subject, body, category }, { withCredentials: true });
      }
      toast.success("Template saved.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} title={isNew ? "New saved template" : "Edit template"} testId="saved-modal">
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Welcome tutorial — Week 1" data-testid="saved-name" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md bg-white">
            <option value="general">General</option>
            <option value="tutorial">Tutorial</option>
            <option value="marketing">Marketing</option>
            <option value="onboarding">Onboarding</option>
            <option value="announcement">Announcement</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Subject</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="saved-subject" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 block mb-1">Body</label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="text-sm" data-testid="saved-body" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="saved-save-btn">
          <Save className="w-3.5 h-3.5" />{saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </ModalShell>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Shared modal shell — keeps consistent look across all the editors
// ──────────────────────────────────────────────────────────────────────

function ModalShell({ title, onClose, testId, size = "md", children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid={testId}>
      <div
        className={`bg-white rounded-xl shadow-xl border border-gray-200 w-full ${size === "lg" ? "max-w-3xl" : "max-w-xl"} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4">
          {children}
        </div>
      </div>
    </div>
  );
}
